import { useQuery } from "@tanstack/react-query";
import { manageMembershipsByAdmin } from "../../../queryOptions/ManageMembership";
import { Membership } from "../../../schemas/memberships";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PauseIcon from "@mui/icons-material/Pause";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import { ViewMembershipDialog } from "./ViewMembershipDialog";
import { toast } from "react-toastify";
import {
  cancelMembership,
  pauseMembership,
  resumeMembership,
  updateAdminMembership,
} from "../../../apis/membershipApi";
import { styled } from "@mui/material/styles";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-cell": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "& .MuiDataGrid-toolbarContainer": {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
  },
}));

const ViewMemberships = () => {
  const { data, isLoading, error, refetch } = useQuery(
    manageMembershipsByAdmin()
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedMembership, setSelectedMembership] =
    useState<Membership | null>(null);
  const [membershipId, setMembershipId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null); // New state for filter

  // Handlers (unchanged logic, added accessibility)
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    membershipId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setMembershipId(membershipId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMembershipId(null);
  };

  const handleViewDetails = (membership: Membership) => {
    setSelectedMembership(membership);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleCancelMembership = (membership: Membership) => {
    setSelectedMembership(membership);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmCancellation = async (membership: Membership) => {
    setIsCancelling(true);
    try {
      await cancelMembership(membership.id, membership.stripeSubscriptionId);
      toast.success("Membership cancelled successfully", { autoClose: 3000 });
      refetch();
    } catch (error) {
      console.error("Error cancelling membership:", error);
      toast.error("Failed to cancel membership. Please try again.", {
        autoClose: 5000,
      });
    } finally {
      setIsCancelling(false);
      setDeleteDialogOpen(false);
      setSelectedMembership(null);
      refetch();
    }
  };

  const handlePauseMembership = async (
    membershipId: string,
    subscriptionId: string
  ) => {
    setIsPausing(true);
    try {
      await pauseMembership(membershipId, subscriptionId);
      toast.success("Membership paused successfully", { autoClose: 3000 });
      refetch();
    } catch (error) {
      console.error("Error pausing membership:", error);
      toast.error("Failed to pause membership. Please try again.", {
        autoClose: 5000,
      });
    } finally {
      setIsPausing(false);
      handleMenuClose();
    }
  };

  const handleResumeMembership = async (
    membershipId: string,
    subscriptionId: string
  ) => {
    setIsResuming(true);
    try {
      await resumeMembership(membershipId, subscriptionId);
      toast.success("Membership resumed successfully", { autoClose: 3000 });
      refetch();
    } catch (error) {
      console.error("Error resuming membership:", error);
      toast.error("Failed to resume membership. Please try again.", {
        autoClose: 5000,
      });
    } finally {
      setIsResuming(false);
      handleMenuClose();
    }
  };

  // Format and filter rows for DataGrid
  // Keep amount as number and startDate as Date so column sorts are numeric/chronological
  const rows =
    data
      ?.map((membership: Membership) => ({
        id: membership.id.toUpperCase(),
        customer: `${membership.user.firstName} ${membership.user.lastName}`,
        plan: membership.membershipListing.name,
        amount:
          membership.amount == null ? null : Number(membership.amount),
        startDate: membership.startDate
          ? new Date(membership.startDate).getTime()
          : null,
        nextPaymentDate: membership.nextPaymentDate
          ? new Date(membership.nextPaymentDate).toDateString()
          : "-",
        renewalDate: membership.startDate === membership.renewalDate ? "-" : membership.renewalDate
          ? new Date(membership.renewalDate).toDateString()
          : "-",
        resumeDate: membership.resumeDate
          ? new Date(membership.resumeDate).toDateString()
          : "-",
        pauseDate: membership.pauseDate
          ? new Date(membership.pauseDate).toDateString()
          : "-",
        status: membership.status,
        paymentStatus:
          membership.paymentStatus === null ? "-" : membership.paymentStatus,
        cancelledAt: membership.cancelledAt
          ? new Date(membership.cancelledAt).toDateString()
          : "-",
        membership,
      }))
      .filter((row: any) => {
        if (!selectedFilter) return true; // Show all if no filter is selected
        return (
          row.paymentStatus.toLowerCase() === selectedFilter.toLowerCase() ||
          row.status.toLowerCase() === selectedFilter.toLowerCase()
        );
      }) || [];

  // Define columns with enhanced rendering
  const columns: GridColDef[] = [
    { field: "id", headerName: "Membership ID", width: 150, minWidth: 130 },
    {
      field: "customer",
      headerName: "Customer",
      width: 180,
      minWidth: 140,
      sortingOrder: ["asc", "desc"],
    },
    { field: "plan", headerName: "Plan", width: 160, minWidth: 120 },
    {
      field: "amount",
      headerName: "Amount",
      width: 110,
      minWidth: 90,
      type: "number",
      align: "left",
      headerAlign: "left",
      sortingOrder: ["asc", "desc"],
      valueFormatter: (value: number | null) =>
        value == null || Number.isNaN(Number(value))
          ? "-"
          : `$${Number(value).toFixed(2)}`,
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 150,
      minWidth: 130,
      sortingOrder: ["asc", "desc"],
      valueFormatter: (value: number | null) =>
        value == null ? "-" : new Date(value).toDateString(),
      sortComparator: (v1, v2) => {
        if (v1 == null && v2 == null) return 0;
        if (v1 == null) return 1;
        if (v2 == null) return -1;
        return Number(v1) - Number(v2);
      },
    },
    {
      field: "nextPaymentDate",
      headerName: "Next Payment",
      width: 150,
      minWidth: 130,
    },
    // {
    //   field: "renewalDate",
    //   headerName: "Renewed On",
    //   width: 150,
    //   flex: 1,
    // },
    { field: "resumeDate", headerName: "Date Resumed", width: 150, minWidth: 130 },
    { field: "pauseDate", headerName: "Date Paused", width: 150, minWidth: 130 },
    {
      field: "status",
      headerName: "Membership Status",
      width: 160,
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "active"
              ? "success"
              : params.value === "paused"
              ? "warning"
              : params.value === "resumed"
              ? "info"
              : "error"
          }
          size="small"
        />
      ),
    },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      width: 150,
      minWidth: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "confirmed"
              ? "success"
              : params.value === "pay_at_counter"
              ? "warning"
              : "default"
          }
          size="small"
        />
      ),
    },
    { field: "cancelledAt", headerName: "Cancel Date", width: 150, minWidth: 130 },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      minWidth: 70,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const membership = params.row.membership as Membership;
        return (
          <>
            <Tooltip title="More Options">
              <IconButton
                aria-label={`Actions for membership ${membership.id}`}
                onClick={(event) => handleMenuOpen(event, params.id as string)}
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && membershipId === params.id}
              onClose={handleMenuClose}
              TransitionProps={{ timeout: 200 }}
            >
              <MenuItem onClick={() => handleViewDetails(membership)}>
                <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
                View Details
              </MenuItem>
              {/* <MenuItem onClick={() => handleEditStatus(membership)}>
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                Edit Status
              </MenuItem> */}
              {membership.isAdminCreated === null &&
                membership.status !== "cancelled" && (
                  <>
                    <MenuItem
                      onClick={() =>
                        handlePauseMembership(
                          membership.id,
                          membership.stripeSubscriptionId
                        )
                      }
                      disabled={membership.status === "paused" || isPausing}
                    >
                      {isPausing ? (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      ) : (
                        <PauseIcon fontSize="small" sx={{ mr: 1 }} />
                      )}
                      {isPausing ? "Pausing..." : "Pause Membership"}
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleResumeMembership(
                          membership.id,
                          membership.stripeSubscriptionId
                        )
                      }
                      disabled={membership.status !== "paused" || isResuming}
                    >
                      {isResuming ? (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      ) : (
                        <ArrowRightIcon fontSize="small" sx={{ mr: 1 }} />
                      )}
                      {isResuming ? "Resuming..." : "Resume Membership"}
                    </MenuItem>
                  </>
                )}
              {membership.isAdminCreated &&
                membership.status !== "cancelled" && (
                  <>
                    <MenuItem
                      onClick={async () => {
                        setIsPausing(true);
                        try {
                          const response = await updateAdminMembership(membership.id, { status: "paused" })
                          if (response.message === "Membership updated successfully") {
                            toast.success("Membership paused successfully", { autoClose: 3000 });
                            refetch();
                          }
                        } catch (error) {
                          console.error("Error pausing membership:", error);
                          toast.error("Failed to pause membership. Please try again.", {
                            autoClose: 5000,
                          });
                        } finally {
                          setIsPausing(false);
                          handleMenuClose();
                        }
                      }}
                      disabled={membership.status === "paused" || isPausing}
                    >
                      {isPausing ? (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      ) : (
                        <PauseIcon fontSize="small" sx={{ mr: 1 }} />
                      )}
                      {isPausing ? "Pausing..." : "Pause Membership"}
                    </MenuItem>
                    <MenuItem
                      onClick={async () =>{
                        setIsResuming(true);
                        try {
                          const response = await updateAdminMembership(
                            membership.id,
                            { status: "resumed" }
                          )
                          if (response.message === "Membership updated successfully") {
                            toast.success("Membership resumed successfully", { autoClose: 3000 });
                            refetch();
                          }
                        } catch (error) {
                          console.error("Error resuming membership:", error);
                          toast.error("Failed to resume membership. Please try again.", {
                            autoClose: 5000,
                          });
                        } finally {
                          setIsResuming(false);
                          handleMenuClose();
                        }
                      }}
                      disabled={membership.status !== "paused" || isResuming}
                    >
                      {isResuming ? (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      ) : (
                        <ArrowRightIcon fontSize="small" sx={{ mr: 1 }} />
                      )}
                      {isResuming ? "Resuming..." : "Resume Membership"}
                    </MenuItem>
                  </>
                )}
              {membership.status !== "cancelled" && (
                <MenuItem onClick={() => handleCancelMembership(membership)}>
                  <DeleteIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "error.main" }}
                  />
                  Cancel Membership
                </MenuItem>
              )}
            </Menu>
          </>
        );
      },
    },
  ];

    if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          No memberships found
        </Typography>
        <Typography variant="body1" color="text.secondary">
          There are currently no memberships to display.
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error">
          Failed to load memberships. Please try again later.
        </Typography>
        <Button variant="contained" onClick={() => refetch()} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body1" gutterBottom>
        Filter by status:
      </Typography>
      <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
        {[
          "Confirmed",
          "Pay_At_Counter",
          "Cancelled",
          "Resumed",
          "Paused",
          "Active",
          "Pending",
        ].map((status) => (
          <Button
            key={status}
            variant={selectedFilter === status ? "contained" : "outlined"}
            onClick={() =>
              setSelectedFilter(selectedFilter === status ? null : status)
            }
          >
            {status}
          </Button>
        ))}
        <Button
          variant={selectedFilter === null ? "contained" : "outlined"}
          onClick={() => setSelectedFilter(null)}
        >
          All
        </Button>
      </Box>
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          sortingOrder={["asc", "desc"]}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: "nextPaymentDate", sort: "desc" }] },
          }}
          getRowId={(row) => row.id}
          autoHeight
          sx={{
            bgcolor: "white",
            "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "nowrap",
            },
            "& .MuiDataGrid-toolbarContainer": {
              flexWrap: "wrap",
              gap: 1,
            },
            "& .MuiDataGrid-virtualScroller": {
              overflowY: "hidden",
            },
            "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
              display: "none",
            },
            "& .MuiDataGrid-scrollbar--vertical": {
              display: "none",
            },
          }}
        />
      </Box>
      <ViewMembershipDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedMembership(null);
        }}
        selectedMembership={selectedMembership}
      />
      <StyledDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="cancel-membership-dialog"
      >
        <DialogTitle id="cancel-membership-dialog">
          Cancel Membership
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to cancel this membership? This action cannot
            be undone.
          </Typography>
          {selectedMembership && (
            <Paper sx={{ p: 2, mt: 2, bgcolor: "grey.50" }}>
              <Typography variant="body2">
                <strong>Customer:</strong> {selectedMembership.user.firstName}{" "}
                {selectedMembership.user.lastName}
              </Typography>
              <Typography variant="body2">
                <strong>Plan:</strong>{" "}
                {selectedMembership.membershipListing.name}
              </Typography>
              <Typography variant="body2">
                <strong>Start Date:</strong>{" "}
                {new Date(selectedMembership.startDate).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                Cancelling will stop future charges and revoke access to
                membership benefits.
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isCancelling}
            aria-label="Keep membership"
          >
            Keep Membership
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() =>
              handleConfirmCancellation(selectedMembership as Membership)
            }
            disabled={isCancelling}
            startIcon={isCancelling ? <CircularProgress size={20} /> : null}
            aria-label="Confirm cancellation"
          >
            {isCancelling ? "Cancelling..." : "Cancel Membership"}
          </Button>
        </DialogActions>
      </StyledDialog>
      {/* <EditStatus
        editDialogOpen={editDialogOpen}
        setEditDialogOpen={setEditDialogOpen}
        selectedMembership={selectedMembership}
        handleStatusChange={async (membershipId, newStatus) => {
          try {
            await updateAdminMembership(membershipId, newStatus);
            toast.success("Status updated successfully", { autoClose: 3000 });
            refetch();
          } catch (error) {
            toast.error("Failed to update status. Please try again.", {
              autoClose: 5000,
            });
          } finally {
            setEditDialogOpen(false);
            setSelectedMembership(null);
          }
        }}
      /> */}
    </Box>
  );
};

export default ViewMemberships;
